import { ChangeDetectionStrategy, Component, ElementRef, input, output, signal, viewChild } from '@angular/core';

/**
 * Zone de dépôt de fichier — drag & drop + clic pour parcourir, état sélectionné,
 * refus des extensions hors `accept` (le drag & drop contourne l'attribut accept
 * du navigateur, la vérification est refaite ici). Le contenu vide (consignes,
 * liste des formats) est projeté.
 *
 *   <wh-dropzone accept=".sfb,.bin" (fileSelected)="file.set($event)">
 *     Glisse un firmware ici, ou clique pour parcourir
 *   </wh-dropzone>
 */
@Component({
  selector: 'wh-dropzone',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-dashed p-6 text-center transition-colors"
      [class]="
        dragOver()
          ? 'border-primary-400 bg-primary-50 dark:bg-primary-500/10'
          : file()
            ? 'border-primary-400 dark:border-primary-500 bg-gray-50 dark:bg-slate-900/50'
            : 'border-gray-300 bg-gray-50 dark:border-slate-600 dark:bg-slate-900/50'
      "
      [disabled]="disabled()"
      (click)="inputEl.click()"
      (dragover)="onDragOver($event)"
      (dragleave)="dragOver.set(false)"
      (drop)="onDrop($event)">
      <input #inputEl type="file" [accept]="accept()" class="hidden" (change)="onInputChange($event)" />
      @if (file(); as selected) {
        <span class="font-mono text-sm font-bold text-gray-900 dark:text-slate-100">{{ selected.name }}</span>
        <span class="text-xs text-gray-500 dark:text-slate-400">{{ formatSize(selected.size) }}</span>
      } @else {
        <span class="text-sm font-bold text-gray-700 dark:text-slate-200"><ng-content /></span>
        @if (accept()) {
          <span class="text-xs text-gray-500 dark:text-slate-400">{{ acceptLabel() }}</span>
        }
        <span class="text-[11px] text-gray-400 dark:text-slate-500"><ng-content select="[slot=hint]" /></span>
      }
    </button>
  `,
})
export class WhDropzoneComponent {
  /** Extensions acceptées, format attribut accept (« .sfb,.bin »). Vide = tout. */
  readonly accept = input<string>('');
  readonly disabled = input(false);
  readonly fileSelected = output<File | null>();
  /** Fichier refusé car extension hors accept (à toaster côté page). */
  readonly rejected = output<File>();

  protected readonly inputEl = viewChild.required<ElementRef<HTMLInputElement>>('inputEl');
  protected readonly dragOver = signal(false);
  readonly file = signal<File | null>(null);

  /** Réinitialise la zone (après un upload réussi). */
  clear(): void {
    this.file.set(null);
    this.inputEl().nativeElement.value = '';
  }

  protected acceptLabel(): string {
    return this.accept().split(',').join(' · ');
  }

  protected onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragOver.set(true);
  }

  protected onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragOver.set(false);
    const dropped = event.dataTransfer?.files?.item(0);
    if (dropped) this.take(dropped);
  }

  protected onInputChange(event: Event) {
    const selected = (event.target as HTMLInputElement).files?.item(0);
    if (selected) this.take(selected);
  }

  private take(candidate: File) {
    const accept = this.accept();
    if (accept) {
      const ok = accept
        .split(',')
        .map(ext => ext.trim().toLowerCase())
        .some(ext => candidate.name.toLowerCase().endsWith(ext));
      if (!ok) {
        this.rejected.emit(candidate);
        return;
      }
    }
    this.file.set(candidate);
    this.fileSelected.emit(candidate);
  }

  protected formatSize(size: number): string {
    if (size < 1024) return `${size} o`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  }
}
